import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/text-input";
import { ProjectAvatar } from "@/workshop/components/panels/project-avatar";
import { ColorPicker } from "@/workshop/components/ui/color-picker";
import {
  MOCK_PROJECTS,
  MOCK_SPACES,
  MOCK_AGENTS,
} from "@/workshop/data/mock-project-management";
import type { MockProject, MockSpace, MockAgent } from "@/workshop/data/mock-project-management";

/**
 * Workshop: Enhanced Projects section for Settings (Z-122).
 *
 * List view → detail drill-down pattern, matching the production shape.
 * New additions: ProjectAvatar on list rows, ColorPicker and Spaces list
 * with delete actions in the detail view.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function FieldGroup({
  label,
  children,
  testId,
}: {
  label: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <span className="text-[10px] font-medium uppercase tracking-wider text-secondary">
        {label}
      </span>
      {children}
    </div>
  );
}

/** 2×2 grid of small squares — matches the SpaceIcon in browse-tree. */
function SpaceIcon({ className }: { className?: string }) {
  return (
    <span
      className={cn("inline-grid shrink-0 grid-cols-2 gap-[2px]", className)}
      aria-hidden="true"
    >
      <span className="h-[5px] w-[5px] rounded-[1px] bg-current opacity-70" />
      <span className="h-[5px] w-[5px] rounded-[1px] bg-current opacity-70" />
      <span className="h-[5px] w-[5px] rounded-[1px] bg-current opacity-70" />
      <span className="h-[5px] w-[5px] rounded-[1px] bg-current opacity-70" />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Project List View
// ---------------------------------------------------------------------------

function ProjectListView({
  projects,
  spaces,
  onSelect,
  testId,
}: {
  projects: MockProject[];
  spaces: MockSpace[];
  onSelect: (id: string) => void;
  testId: string;
}) {
  if (projects.length === 0) {
    return (
      <div className="flex items-center justify-center py-8" data-testid={testId}>
        <span className="text-[10px] text-muted">No projects yet</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {projects.map((project) => {
        const spaceCount = spaces.filter((s) => s.projectId === project.id).length;
        return (
          <button
            key={project.id}
            type="button"
            className="flex items-center gap-3 rounded-lg border border-glass-stroke-subtle p-3 text-left hover:bg-btn-ghost-fill"
            onClick={() => onSelect(project.id)}
            data-testid={`project-row-${project.id}`}
          >
            <ProjectAvatar
              name={project.name}
              color={project.color}
              data-testid={`project-avatar-${project.id}`}
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium text-primary">
                  {project.name}
                </span>
                <Badge variant="outline" data-testid={`project-space-count-${project.id}`}>
                  {spaceCount} {spaceCount === 1 ? "space" : "spaces"}
                </Badge>
              </div>
              <span className="text-[10px] text-secondary">
                Created {formatDate(project.createdAt)}
              </span>
            </div>
            <Icons.ChevronRight className="h-4 w-4 shrink-0 text-muted" />
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project Detail View
// ---------------------------------------------------------------------------

function ProjectDetailView({
  project,
  spaces,
  agents,
  onBack,
  testId,
}: {
  project: MockProject;
  spaces: MockSpace[];
  agents: MockAgent[];
  onBack: () => void;
  testId: string;
}) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description);
  const [color, setColor] = useState(project.color);
  const [projectSpaces, setProjectSpaces] = useState<MockSpace[]>(
    spaces.filter((s) => s.projectId === project.id),
  );

  function handleDeleteSpace(spaceId: string) {
    setProjectSpaces((prev) => prev.filter((s) => s.id !== spaceId));
  }

  return (
    <div className="flex flex-col gap-5" data-testid={testId}>
      {/* Back button */}
      <button
        type="button"
        className="flex items-center gap-1.5 text-[11px] text-secondary transition-colors hover:text-primary"
        onClick={onBack}
        data-testid="project-detail-back"
      >
        <Icons.ArrowLeft className="h-3.5 w-3.5" />
        Back to projects
      </button>

      {/* Project name */}
      <FieldGroup label="Project name" testId="project-detail-name-field">
        <TextInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="project-detail-name-input"
        />
      </FieldGroup>

      {/* Project description */}
      <FieldGroup label="Description" testId="project-detail-description-field">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className={cn(
            "w-full resize-none rounded-lg border border-glass-stroke-light bg-glass-fill-heavy",
            "px-3 py-2 text-[11px] text-primary placeholder:text-muted",
            "outline-none transition-colors focus:border-accent",
          )}
          placeholder="Describe this project…"
          data-testid="project-detail-description-input"
        />
      </FieldGroup>

      {/* Color picker */}
      <FieldGroup label="Color" testId="project-detail-color-field">
        <div className="flex items-center gap-2">
          <ColorPicker
            value={color}
            onChange={setColor}
            data-testid="project-detail-color-picker"
          />
          <span className="text-[10px] text-secondary">
            {color}
          </span>
        </div>
      </FieldGroup>

      {/* Spaces list */}
      <FieldGroup label="Spaces" testId="project-detail-spaces">
        {projectSpaces.length === 0 ? (
          <span className="text-[10px] text-muted">No spaces in this project</span>
        ) : (
          <div className="flex flex-col gap-1.5">
            {projectSpaces.map((space) => (
              <div
                key={space.id}
                className="flex items-center gap-3 rounded-lg border border-glass-stroke-subtle p-3"
                data-testid={`project-space-row-${space.id}`}
              >
                <SpaceIcon className="text-secondary" />
                <span className="min-w-0 flex-1 text-[11px] text-primary">
                  {space.name}
                </span>
                <button
                  type="button"
                  className="flex h-6 w-6 items-center justify-center rounded text-muted transition-colors hover:bg-btn-ghost-fill hover:text-danger"
                  onClick={() => handleDeleteSpace(space.id)}
                  data-testid={`project-space-delete-${space.id}`}
                  aria-label={`Remove ${space.name}`}
                >
                  <Icons.Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </FieldGroup>

      {/* Agents (read-only) */}
      <FieldGroup label="Agents" testId="project-detail-agents">
        {agents.length === 0 ? (
          <span className="text-[10px] text-muted">No agents assigned</span>
        ) : (
          <div className="flex flex-col gap-1.5">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center gap-3 rounded-lg border border-glass-stroke-subtle p-3"
                data-testid={`project-agent-${agent.id}`}
              >
                <Icons.Bot className="h-3.5 w-3.5 shrink-0 text-secondary" />
                <span className="text-[11px] text-primary">{agent.name}</span>
              </div>
            ))}
          </div>
        )}
      </FieldGroup>

      {/* Actions */}
      <div
        className="flex items-center gap-2 border-t border-glass-stroke-subtle pt-4"
        data-testid="project-detail-actions"
      >
        <Button
          variant="destructive"
          size="sm"
          data-testid="project-archive-btn"
        >
          Archive project
        </Button>
        <div className="flex-1" />
        <Button size="sm" data-testid="project-save-btn">
          Save
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectsSectionEnhanced (main export)
// ---------------------------------------------------------------------------

interface ProjectsSectionEnhancedProps {
  className?: string;
  "data-testid"?: string;
}

export function ProjectsSectionEnhanced({
  className,
  "data-testid": testId = "settings-projects-enhanced",
}: ProjectsSectionEnhancedProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedProject = selectedId
    ? (MOCK_PROJECTS.find((p) => p.id === selectedId) ?? null)
    : null;

  if (selectedProject) {
    return (
      <div className={className}>
        <ProjectDetailView
          project={selectedProject}
          spaces={MOCK_SPACES}
          agents={MOCK_AGENTS}
          onBack={() => setSelectedId(null)}
          testId={`${testId}-detail`}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <ProjectListView
        projects={MOCK_PROJECTS}
        spaces={MOCK_SPACES}
        onSelect={setSelectedId}
        testId={testId}
      />
    </div>
  );
}

export type { ProjectsSectionEnhancedProps };
